import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { ILead } from '@clickersphere/core/entities/ILead';
import { buildWhatsAppUrl } from '@clickersphere/core/utils/contact-links';
import { renderMessageTemplate } from '@clickersphere/core/utils/message-template';
import { useMessageTemplates } from '../../../../../app/hooks/message-template/useMessageTemplates';
import { useAuth } from '../../../../../app/hooks/useAuth';
import useToast from '../../../../../app/hooks/useToast';
import { useStore } from '../../../../../app/store';
import { resolveLocale } from '../../../../../app/utils/resolveLocale';
import useLanguageSelectorController from '../../../LanguageSelector/useLanguageSelectorController';

export function useMessageComposerController(lead: ILead) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { tenant, loggedInUser } = useAuth();
  const { selectedLanguage } = useLanguageSelectorController();
  const { dateOnlyFormat } = resolveLocale(selectedLanguage);
  const { messageTemplates, isMessageTemplatesLoading } = useMessageTemplates();

  const { handleCloseModal } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const templateOptions = useMemo(
    () =>
      messageTemplates.map((template) => ({
        value: template.id,
        text: template.name,
      })),
    [messageTemplates],
  );

  const nextEvent = useMemo(() => {
    const events = lead.events ?? [];
    const now = Date.now();
    const upcoming = events
      .filter((event) => event.date && new Date(event.date).getTime() >= now)
      .sort(
        (a, b) =>
          new Date(a.date as string).getTime() -
          new Date(b.date as string).getTime(),
      );
    return upcoming[0] ?? events[events.length - 1];
  }, [lead.events]);

  const variables = useMemo<Record<string, string | undefined>>(
    () => ({
      lead_nome: lead.name,
      lead_telefone: lead.phone,
      lead_email: lead.email,
      evento_descricao: nextEvent?.description,
      evento_data: nextEvent?.date
        ? format(new Date(nextEvent.date), dateOnlyFormat)
        : undefined,
      evento_local: nextEvent?.location,
      fotografo: loggedInUser?.name,
      estudio: tenant?.name,
    }),
    [lead, nextEvent, dateOnlyFormat, loggedInUser?.name, tenant?.name],
  );

  const selectedTemplate = messageTemplates.find(
    (template) => template.id === selectedTemplateId,
  );

  const renderedMessage = selectedTemplate
    ? renderMessageTemplate(selectedTemplate.body, variables)
    : '';

  const whatsAppUrl = buildWhatsAppUrl(lead.phone, renderedMessage);

  async function handleCopy() {
    if (!renderedMessage) return;
    try {
      await navigator.clipboard.writeText(renderedMessage);
      toast(t('lead.messageComposer.copied'), 'success');
    } catch {
      toast(t('lead.messageComposer.copyError'), 'error');
    }
  }

  function handleOpenWhatsApp() {
    if (!whatsAppUrl) return;
    window.open(whatsAppUrl, '_blank', 'noopener,noreferrer');
  }

  return {
    t,
    templateOptions,
    selectedTemplateId,
    setSelectedTemplateId,
    renderedMessage,
    hasTemplates: messageTemplates.length > 0,
    isMessageTemplatesLoading,
    canSendWhatsApp: !!whatsAppUrl,
    hasPhone: !!lead.phone,
    handleCopy,
    handleOpenWhatsApp,
    handleClose: handleCloseModal,
  };
}
